import { useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

// Admin-only inline editor for a physician's NPI. Admins may change an NPI at
// any time (unlike clinics, which are locked once verified — enforced on the
// backend). Self-contained: persists via updateUserProfile and reflects the
// saved value locally, mirroring TelemedNetworkSection's pattern.
export function AdminNpiEditor({ physicianId, currentNpi }: { physicianId: string; currentNpi?: string }) {
  const [saved, setSaved] = useState(currentNpi || '');
  const [draft, setDraft] = useState(currentNpi || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = draft.trim();
    if (trimmed && !/^\d{10}$/.test(trimmed)) {
      toast.error('NPI must be exactly 10 digits');
      return;
    }
    if (trimmed === saved) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const res = await apiClient.updateUserProfile(physicianId, { npiNumber: trimmed });
    setSaving(false);
    if (res.success) {
      setSaved(trimmed);
      setEditing(false);
      toast.success('NPI updated');
    } else {
      toast.error(res.error || 'Failed to update NPI');
    }
  };

  const cancel = () => {
    setDraft(saved);
    setEditing(false);
  };

  return (
    <div>
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">NPI Number</div>
      {editing ? (
        <div className="mt-1 flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="10-digit NPI"
            inputMode="numeric"
            maxLength={10}
            autoFocus
            className="w-40 px-2 py-1 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
            title="Save"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="mt-1 flex items-center gap-2">
          <span className="text-sm text-gray-900 font-mono">
            {saved || <span className="text-gray-400 italic font-sans">Not set</span>}
          </span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="p-1 text-gray-400 hover:text-blue-600"
            title="Edit NPI"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminNpiEditor;
